import type { RouterConfig } from "@nuxt/schema";

const LAYOUT_SETTLE_TIMEOUT_MS = 1000;

// Vue Router calcule la position via getBoundingClientRect() puis appelle
// window.scrollTo() — ce qui ignore scroll-padding-top. On compense
// manuellement avec l'option `top` pour respecter la hauteur du header sticky.
function headerOffset(): number {
  if (typeof window === "undefined") return 0;
  const value = getComputedStyle(document.documentElement).getPropertyValue("--ui-header-height");
  return parseInt(value) || 72;
}

function maxScrollTop(): number {
  return Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
}

function nextFrame(): Promise<void> {
  return new Promise((resolve) => requestAnimationFrame(() => resolve()));
}

async function waitForLayout(isReady: () => boolean): Promise<void> {
  const deadline = performance.now() + LAYOUT_SETTLE_TIMEOUT_MS;
  while (!isReady() && performance.now() < deadline) await nextFrame();
}

export default <RouterConfig>{
  async scrollBehavior(to, from, savedPosition) {
    const nuxtApp = useNuxtApp();
    const isSamePath = to.path.replace(/\/$/, "") === from.path.replace(/\/$/, "");

    if (isSamePath && !savedPosition) {
      if (to.hash) {
        return { el: to.hash, top: headerOffset(), behavior: "smooth" };
      }
      if (from.hash) {
        return { left: 0, top: 0, behavior: "instant" };
      }
      if (to.query.page !== from.query.page) {
        return { left: 0, top: 0, behavior: "instant" };
      }
      return false;
    }

    if (!isSamePath) {
      await new Promise<void>((resolve) => nuxtApp.hooks.hookOnce("page:loading:end", () => resolve()));
    }
    await nextFrame();

    if (savedPosition) {
      // Le contenu asynchrone (données, images) arrive après `page:loading:end` : tant que le
      // document est plus court que la position visée, window.scrollTo() serait tronqué au bas.
      const restoredTop = savedPosition.top;
      await waitForLayout(() => maxScrollTop() >= restoredTop);
      return { ...savedPosition, behavior: "instant" };
    }
    if (to.hash) {
      const anchorId = to.hash.slice(1);
      await waitForLayout(() => document.getElementById(anchorId) !== null);
      return { el: to.hash, top: headerOffset(), behavior: "instant" };
    }
    return { left: 0, top: 0, behavior: "instant" };
  },
};
